import { useEffect, useMemo } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { LoadingScreen } from 'src/components/loading-screen';
import { useStores } from 'src/models';
import { PATH_AUTH, PATH_PAGE } from 'src/routes/paths';

type AuthGuardProps = {
  children: React.ReactNode;
};

function AuthGuard({ children }: AuthGuardProps) {
  const { authStore } = useStores();
  const { isAuthenticated, isInitialized } = authStore;

  const { pathname } = useLocation();
  const navigate = useNavigate();

  // 금연일 설정 > FTND > 문진 > 복용약 문진 > 서비스 이용 가능
  const requireSurveyPaths = useMemo(
    () => [
      PATH_PAGE.home,
      PATH_PAGE.drJin.root,
      PATH_PAGE.mission.root,
      PATH_PAGE.craving.root,
      PATH_PAGE.calendar.root,
    ],
    [],
  );

  const excludedPaths = useMemo(
    () => [
      PATH_PAGE.quitSmokingDateSettings,
      PATH_PAGE.ftnd.root,
      PATH_PAGE.medicalQuestion.root,
      PATH_PAGE.myPage.root,
      PATH_PAGE.drugQuestion.root,
      PATH_PAGE.medicineSettings.root,
    ],
    [],
  );

  useEffect(() => {
    const checkHomeAccess = async () => {
      const userProfile = authStore.user;
      if (!userProfile) return;

      // 예외 경로 or 문진 필수 경로가 아닌 경우 return;
      if (
        excludedPaths.some((path) => pathname.startsWith(path)) ||
        !requireSurveyPaths.some((path) => pathname.startsWith(path))
      ) {
        return;
      }

      // 문진 완료 여부
      const hasIncompleteMedQuestions = userProfile.medicalQuestionCompleteYn?.some(
        (item) => item.medicalQuestionCompleteYn === 'N',
      );

      // 복용약 문진 완료 여부
      const hasIncompleteDrugQuestions = userProfile.drugQuestionCompleteYn?.some(
        (item) => item.drugQuestionCompleteYn === 'N',
      );

      let redirectPath = null;
      if (!userProfile.quitDt) {
        redirectPath = PATH_PAGE.quitSmokingDateSettings;
      } else if (userProfile.ftndCompleteYn === 'N') {
        redirectPath = PATH_PAGE.ftnd.root;
      } else if (hasIncompleteMedQuestions) {
        redirectPath = PATH_PAGE.medicalQuestion.root;
      } else if (hasIncompleteDrugQuestions) {
        redirectPath = PATH_PAGE.drugQuestion.root;
      }

      if (redirectPath && pathname !== redirectPath) {
        navigate(redirectPath);
      }
    };

    checkHomeAccess();
  }, [
    authStore.user,
    navigate,
    pathname,
    isInitialized,
    isAuthenticated,
    excludedPaths,
    requireSurveyPaths,
  ]);

  if (!isInitialized) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to={PATH_AUTH.login} replace state={{ from: pathname }} />;
  }

  // Page 래핑 제거: children이 이미 자체 <Page> 사용하므로 중첩 렌더 방지
  // (CSnackbar/CInfoModal 중복 렌더로 인한 알림 2회 표시 버그 해결)
  return <>{children}</>;
}

export default observer(AuthGuard);
